import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Button from './Button';
import Icon from '../AppIcon';

const ConsultationCTA = ({ 
  variant = 'floating', 
  context = 'general',
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (variant === 'floating') {
      const handleScroll = () => {
        const scrolled = window.scrollY > 300;
        setIsScrolled(scrolled);
        setIsVisible(scrolled && window.scrollY < document.body.scrollHeight - window.innerHeight - 200);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setIsVisible(true);
    }
  }, [variant]);

  const getContextualMessage = () => {
    const messages = {
      services: {
        title: 'Ready to Transform Your Business?',
        subtitle: 'Discuss your AI implementation strategy',
        cta: 'Schedule Strategy Call'
      },
      about: {
        title: 'Partner with AI Experts',
        subtitle: 'Let\'s explore how we can help your organization',
        cta: 'Start Conversation'
      },
      team: {
        title: 'Work with Our Experts',
        subtitle: 'Connect with our AI consultants today',
        cta: 'Meet the Team'
      },
      contact: {
        title: 'Get Started Today',
        subtitle: 'Book your free consultation now',
        cta: 'Book Consultation'
      },
      general: {
        title: 'Ready to Get Started?',
        subtitle: 'Schedule your free AI consultation',
        cta: 'Book Consultation'
      }
    };

    return messages[context] || messages.general;
  };

  const handleConsultationClick = () => {
    const currentService = location.pathname.includes('service') ? 'AI Implementation' : '';
    const params = new URLSearchParams();
    if (currentService) params.set('service', currentService);
    if (context !== 'general') params.set('source', context);
    
    window.location.href = `/contact-consultation-request${params.toString() ? '?' + params.toString() : ''}`;
  };

  const message = getContextualMessage();

  if (variant === 'floating') {
    return (
      <div 
        className={`fixed bottom-6 right-6 z-40 transform transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-white rounded-xl shadow-elevation-lg border border-border p-4 max-w-sm">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
              <Icon name="MessageSquare" size={20} color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-foreground text-sm mb-1">
                {message.title}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {message.subtitle}
              </p>
              <Button 
                variant="default" 
                size="sm" 
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                onClick={handleConsultationClick}
              >
                {message.cta}
              </Button>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="text-muted-foreground hover:text-foreground transition-smooth p-1"
              aria-label="Close consultation prompt"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6 border border-primary/10 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
              <Icon name="Zap" size={24} color="white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg mb-1">
                {message.title}
              </h3>
              <p className="text-muted-foreground">
                {message.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/contact-consultation-request'}
            >
              Learn More
            </Button>
            <Button 
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              onClick={handleConsultationClick}
            >
              {message.cta}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`gradient-primary text-white rounded-xl p-8 text-center ${className}`}>
        <div className="max-w-2xl mx-auto">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Icon name="Rocket" size={32} color="white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {message.title}
          </h2>
          <p className="text-white/90 mb-6 text-lg">
            {message.subtitle}
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button 
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = '/services-overview'}
            >
              Explore Services
            </Button>
            <Button 
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={handleConsultationClick}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              {message.cta}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ConsultationCTA;